"use client"
import { useState, useCallback, useEffect } from "react"
import { useParams } from "react-router-dom"
import { Edit2, Plus, X, Save, Loader, FileText, Film, FileQuestion, AlertCircle, ArrowLeft, Eye, ArrowUpRight, CloudUpload, BookOpen, WandIcon, Users, Check, MoreVertical, ChevronDown, ChevronRight, Clock, CheckCircle, XCircle } from 'lucide-react'
import { getCourseDetials, getClassData } from "./api"
import { postAws } from "../postAws"
import { postTeacherElearningCourseVideo, generatePresignedUrl } from "../postFileAws"
import { Toast, TruncatedDescription, getContentIcon, extractYoutubeVideoId } from "./ui-components"
import { TopicManagement } from "./topic-management"
import { ContentManagement } from "./content-management"
import { AIFeatures } from "./ai-features"
import {
  ContentMenuModal,
  ContentModal,
  EditSubtopicModal,
  CourseOutlineConfirmationModal,
} from "./elearning-course-modals"
import { CourseQuizEditModal } from "./course-quiz-edit-modal"
import { EmptyCourseView } from "./empty-course-view"
import { NotesEditorModal } from "./notes-editor-modal"

export default function ELearningCourseEdit() {
  const [course, setCourse] = useState(null)
  const [unsavedTopics, setUnsavedTopics] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [toast, setToast] = useState(null)
  const [currentTopic, setCurrentTopic] = useState(null)
  const [isSaving, setIsSaving] = useState(false)
  const [previewMode, setPreviewMode] = useState(false)
  const [activeContentTab, setActiveContentTab] = useState(null)
  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [editedTitle, setEditedTitle] = useState("")
  const [editedDescription, setEditedDescription] = useState("")
  const [quizToEdit, setQuizToEdit] = useState(null)
  const [isEditingDescription, setIsEditingDescription] = useState(false)
  const [publishTooltipContent, setPublishTooltipContent] = useState(null)
  const [showMobileMenu, setShowMobileMenu] = useState(false)

  // Enhanced YouTube notes creation states
  const [showNotesDialog, setShowNotesDialog] = useState(false)
  const [notesTitle, setNotesTitle] = useState("")
  const [currentVideoData, setCurrentVideoData] = useState(null)
  const [isCreatingNotes, setIsCreatingNotes] = useState(false)
  const [notesCreationStep, setNotesCreationStep] = useState("")
  const [createdNotesId, setCreatedNotesId] = useState(null)

  // Notes status tracking states
  const [notesStatusPolling, setNotesStatusPolling] = useState(new Set())
  const [notesPollingIntervals, setNotesPollingIntervals] = useState(new Map())

  // New states for student sharing
  const [showStudentSelectionModal, setShowStudentSelectionModal] = useState(false)
  const [enrolledStudents, setEnrolledStudents] = useState([])
  const [selectedStudents, setSelectedStudents] = useState([])
  const [isLoadingStudents, setIsLoadingStudents] = useState(false)
  const [isSharing, setIsSharing] = useState(false)

  const { courseId } = useParams()

  const showToast = (message, type = "success") => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 5000)
  }

  const updateCourse = useCallback((updater) => {
    setCourse(updater)
  }, [])

  // Enhanced notes status polling function
  const startNotesStatusPolling = useCallback((subtopicId) => {
    if (notesStatusPolling.has(subtopicId)) return

    setNotesStatusPolling(prev => new Set([...prev, subtopicId]))
    
    const pollInterval = setInterval(async () => {
      try {
        const response = await postAws("https://api-dev.aurait.ai/eLearning", "POST", {
          request: "get-notes-status",
          body: {
            courseId: courseId,
            subtopicId: subtopicId,
          },
        })

        if (response.statusCode === 200) {
          const { status, pdfUrl, rawJsonUrl } = response.body.data
          
          if (status === "completed") {
            // Update the course data with the completed notes
            updateCourse(prev => ({
              ...prev,
              topics: prev.topics.map(topic => ({
                ...topic,
                content: topic.content.map(item => 
                  item.id === subtopicId 
                    ? { 
                        ...item, 
                        notesStatus: "completed",
                        url: pdfUrl,
                        s3Uri: pdfUrl,
                        rawJsonUrl: rawJsonUrl
                      }
                    : item
                )
              }))
            }))

            showToast("Notes have been created successfully! You can now view and edit them.", "success")
            
            // Clear polling for this subtopic
            clearInterval(pollInterval)
            setNotesStatusPolling(prev => {
              const newSet = new Set(prev)
              newSet.delete(subtopicId)
              return newSet
            })
            setNotesPollingIntervals(prev => {
              const newMap = new Map(prev)
              newMap.delete(subtopicId)
              return newMap
            })
          } else if (status === "failed") {
            showToast("Notes creation failed. Please try again.", "error")
            
            // Update status to failed
            updateCourse(prev => ({
              ...prev,
              topics: prev.topics.map(topic => ({
                ...topic,
                content: topic.content.map(item => 
                  item.id === subtopicId 
                    ? { ...item, notesStatus: "failed" }
                    : item
                )
              }))
            }))

            // Clear polling
            clearInterval(pollInterval)
            setNotesStatusPolling(prev => {
              const newSet = new Set(prev)
              newSet.delete(subtopicId)
              return newSet
            })
          }
        }
      } catch (error) {
        console.error("Error polling notes status:", error)
      }
    }, 10000) // Poll every 10 seconds

    setNotesPollingIntervals(prev => new Map([...prev, [subtopicId, pollInterval]]))

    // Auto-clear polling after 5 minutes to prevent infinite polling
    setTimeout(() => {
      clearInterval(pollInterval)
      setNotesStatusPolling(prev => {
        const newSet = new Set(prev)
        newSet.delete(subtopicId)
        return newSet
      })
      setNotesPollingIntervals(prev => {
        const newMap = new Map(prev)
        newMap.delete(subtopicId)
        return newMap
      })
    }, 300000) // 5 minutes
  }, [courseId, updateCourse, notesStatusPolling])

  const fetchCourseDetails = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await getCourseDetials(courseId)
      const courseData = JSON.parse(response)
      if (!courseData || !courseData.course) {
        throw new Error("Invalid course data structure")
      }

      const course = courseData.course
      let thumbnailUrl = null
      if (course.thumbnail && course.thumbnail.startsWith("s3://")) {
        try {
          thumbnailUrl = await generatePresignedUrl(course.thumbnail)
        } catch (error) {
          console.error("Error generating presigned URL for thumbnail:", error)
        }
      }

      const processedTopics = await Promise.all(
        (course.topics || []).map(async (topic) => {
          const processedSubtopics = await Promise.all(
            (topic.subtopics || []).map(async (subtopic) => {
              let contentUrl = subtopic.contentUrl || subtopic.videoUrl || subtopic.pdfUrl
              let contentType = subtopic.contentType || "video"
              const isYoutubeVideo =
                subtopic.isYoutubeVideo ||
                (contentUrl && (contentUrl.includes("youtube.com") || contentUrl.includes("youtu.be")))
              let youtubeVideoId = null

              if (!subtopic.contentType) {
                if (subtopic.pdfUrl) {
                  contentType = "pdf"
                  contentUrl = subtopic.pdfUrl
                } else if (subtopic.quizData) {
                  contentType = "quiz"
                }
              }

              if (isYoutubeVideo && contentUrl) {
                youtubeVideoId = extractYoutubeVideoId(contentUrl)
              } else if (contentUrl && contentUrl.startsWith("s3://")) {
                try {
                  contentUrl = await generatePresignedUrl(contentUrl)
                } catch (error) {
                  console.error("Error generating presigned URL for:", contentUrl, error)
                }
              }

              let parsedQuizData = null
              if (subtopic.quizData) {
                try {
                  parsedQuizData =
                    typeof subtopic.quizData === "string" ? JSON.parse(subtopic.quizData) : subtopic.quizData
                } catch (error) {
                  console.error("Error parsing quiz data:", error)
                }
              }

              const processedSubtopic = {
                id: subtopic.id,
                title: subtopic.title,
                description: subtopic.description || "",
                type: contentType,
                url: contentUrl,
                s3Uri: subtopic.contentUrl || subtopic.videoUrl || subtopic.pdfUrl,
                isYoutubeVideo: isYoutubeVideo,
                youtubeVideoId: youtubeVideoId,
                quizData: parsedQuizData,
                totalMarks: subtopic.totalMarks || 0,
                quizStatus: subtopic.quizStatus,
                notesStatus: subtopic.notesStatus,
                rawJsonUrl: subtopic.rawJsonUrl, // Add this field for AI-generated notes
              }

              // Start polling for in-progress notes
              if (subtopic.notesStatus === "in-progress") {
                setTimeout(() => startNotesStatusPolling(subtopic.id), 1000)
              }

              return processedSubtopic
            }),
          )

          return {
            id: topic.id,
            title: topic.title,
            isExpanded: true,
            content: processedSubtopics,
          }
        }),
      )

      setCourse({
        id: course._id || course.id,
        name: course.title || "Untitled Course",
        description: course.description || "",
        thumbnail: thumbnailUrl,
        thumbnailS3Uri: course.thumbnail,
        topics: processedTopics,
      })
    } catch (err) {
      console.error("Error fetching course details:", err)
      setError("Failed to load course details. Please try again.")
    } finally {
      setIsLoading(false)
    }
  }, [courseId, startNotesStatusPolling])

  // Fetch enrolled students
  const fetchEnrolledStudents = async () => {
    try {
      setIsLoadingStudents(true)
      const classRoomId = localStorage.getItem("classRoomId")
      if (!classRoomId) {
        throw new Error("Classroom ID not found")
      }

      const classData = await getClassData(classRoomId)
      if (classData && classData.body && classData.body.data && classData.body.data.enrolledStudent) {
        setEnrolledStudents(classData.body.data.enrolledStudent)
      } else {
        throw new Error("No enrolled students found")
      }
    } catch (error) {
      console.error("Error fetching enrolled students:", error)
      showToast("Error fetching enrolled students. Please try again.", "error")
    } finally {
      setIsLoadingStudents(false)
    }
  }

  useEffect(() => {
    // Set up tooltip behavior
    const button = document.querySelector('[data-tooltip-id="publish-tooltip"]')
    const tooltip = document.getElementById("publish-tooltip")
    if (button && tooltip) {
      const showTooltip = () => {
        if (publishTooltipContent) {
          const rect = button.getBoundingClientRect()
          tooltip.style.display = "block"
          tooltip.style.top = `${rect.bottom + window.scrollY + 10}px`
          tooltip.style.left = `${rect.left + window.scrollX - 200}px`
        }
      }
      const hideTooltip = () => {
        tooltip.style.display = "none"
      }

      button.addEventListener("mouseenter", showTooltip)
      button.addEventListener("mouseleave", hideTooltip)
      return () => {
        button.removeEventListener("mouseenter", showTooltip)
        button.removeEventListener("mouseleave", hideTooltip)
      }
    }
  }, [publishTooltipContent])

  useEffect(() => {
    if (courseId) {
      fetchCourseDetails()
    }
  }, [courseId, fetchCourseDetails])

  // Cleanup polling intervals on unmount
  useEffect(() => {
    return () => {
      notesPollingIntervals.forEach((interval) => {
        clearInterval(interval)
      })
    }
  }, [notesPollingIntervals])

  const handleSequenceChange = async (updatedTopics) => {
    try {
      setIsSaving(true)
      console.log("function call hua")
      const formattedTopics = updatedTopics.map((topic) => ({
        id: topic.id,
        title: topic.title,
        subtopics: topic.content
          ? topic.content.map((item) => ({
              id: item.id,
              title: item.title,
              description: item.description || "",
              contentType: item.type,
              contentUrl: item.s3Uri,
              videoUrl: item.type === "video" ? item.s3Uri : undefined,
              pdfUrl: item.type === "pdf" ? item.s3Uri : undefined,
              quizData: item.type === "quiz" && item.quizData ? JSON.stringify(item.quizData) : undefined,
              totalMarks: item.totalMarks || 0,
            }))
          : [],
      }))
      console.log("data ye rha", formattedTopics)
      const response = await postAws("https://api-dev.aurait.ai/eLearning", "POST", {
        request: "edit-course",
        body: {
          courseId: courseId,
          updatedData: {
            topics: formattedTopics,
          },
        },
      })
      console.log("reponse ye rha", response)
      if (response.statusCode === 200) {
        setUnsavedTopics([])
        showToast("Course changes saved automatically")
      } else {
        throw new Error(response.body.message || "Failed to save course changes")
      }
    } catch (error) {
      console.error("Error saving course changes:", error)
      showToast("Error saving course changes. Please try again.", "error")
    } finally {
      setIsSaving(false)
    }
  }

  const handleSaveTitle = async () => {
    setIsSaving(true)
    try {
      const response = await postAws("https://api-dev.aurait.ai/eLearning", "POST", {
        request: "edit-course",
        body: {
          courseId: courseId,
          updatedData: {
            title: editedTitle,
          },
        },
      })
      if (response.statusCode === 200) {
        updateCourse((prev) => ({ ...prev, name: editedTitle }))
        setIsEditingTitle(false)
        showToast("Course title updated successfully")
      } else {
        throw new Error(response.body.message || "Failed to update course title")
      }
    } catch (error) {
      console.error("Error updating course title:", error)
      showToast("Error updating course title. Please try again.", "error")
    } finally {
      setIsSaving(false)
    }
  }

  const handleSaveDescription = async () => {
    setIsSaving(true)
    try {
      const response = await postAws("https://api-dev.aurait.ai/eLearning", "POST", {
        request: "edit-course",
        body: {
          courseId: courseId,
          updatedData: {
            description: editedDescription,
          },
        },
      })
      if (response.statusCode === 200) {
        updateCourse((prev) => ({ ...prev, description: editedDescription }))
        setIsEditingDescription(false)
        showToast("Course description updated successfully")
      } else {
        throw new Error(response.body.message || "Failed to update course description")
      }
    } catch (error) {
      console.error("Error updating course description:", error)
      showToast("Error updating course description. Please try again.", "error")
    } finally {
      setIsSaving(false)
    }
  }

  const handleThumbnailUpload = () => {
    const input = document.createElement("input")
    input.type = "file"
    input.accept = "image/*"
    input.onchange = async (e) => {
      const file = e.target.files[0]
      if (!file) return

      try {
        setIsSaving(true)
        const teacherId = localStorage.getItem("teacherId")
        const classRoomId = localStorage.getItem("classRoomId")
        if (!teacherId || !classRoomId) {
          throw new Error("Teacher ID or Classroom ID not found")
        }

        const uploadResult = await postTeacherElearningCourseVideo(
          file,
          teacherId,
          classRoomId,
          file.name,
          course.name,
          "thumbnail",
          "thumbnail",
        )

        if (!uploadResult || !uploadResult.uri) {
          throw new Error("Failed to upload thumbnail")
        }

        const presignedUrl = await generatePresignedUrl(uploadResult.uri)

        const response = await postAws("https://api-dev.aurait.ai/eLearning", "POST", {
          request: "edit-course",
          body: {
            courseId: courseId,
            updatedData: {
              thumbnail: uploadResult.uri,
            },
          },
        })

        if (response.statusCode === 200) {
          updateCourse((prev) => ({
            ...prev,
            thumbnail: presignedUrl,
            thumbnailS3Uri: uploadResult.uri,
          }))
          showToast("Thumbnail updated successfully")
        } else {
          throw new Error(response.body.message || "Failed to update thumbnail")
        }
      } catch (error) {
        console.error("Error updating thumbnail:", error)
        showToast("Error updating thumbnail. Please try again.", "error")
      } finally {
        setIsSaving(false)
      }
    }
    input.click()
  }

  // Enhanced Handle Create Notes from Video
  const handleCreateNotesFromVideo = async (youtubeVideoId, topicId, subtopicId) => {
    // Find the topic and subtopic to get their titles
    const allTopics = [...(course?.topics || []), ...unsavedTopics]
    const currentTopicObj = allTopics.find((t) => t.id === topicId)
    const currentSubtopic = currentTopicObj?.content?.find((c) => c.id === subtopicId)

    if (!currentTopicObj || !currentSubtopic) {
      showToast("Error: Could not find topic or subtopic", "error")
      return
    }

    // Set current video data and show dialog
    setCurrentVideoData({
      youtubeVideoId,
      topicId,
      subtopicId,
      topicTitle: currentTopicObj.title,
      subtopicTitle: currentSubtopic.title,
    })
    setNotesTitle(`${currentSubtopic.title} - Notes`)
    setShowNotesDialog(true)
  }

  // Enhanced Handle the actual notes creation process
const handleCreateNotes = async () => {
  if (!notesTitle.trim()) {
    showToast("Please enter a name for the notes", "error")
    return
  }

  try {
    setIsCreatingNotes(true)
    setNotesCreationStep("Fetching video transcript...")
    showToast("Fetching video transcript...", "info")

    // First API call to get YouTube transcript
    const transcriptUrl = `https://youtube-transcript3.p.rapidapi.com/api/transcript?videoId=${currentVideoData.youtubeVideoId}`
    const transcriptOptions = {
      method: "GET",
      headers: {
        "x-rapidapi-key": "b047023a88msh3cd00bb8613500ep1383d1jsn0328faa11907",
        "x-rapidapi-host": "youtube-transcript3.p.rapidapi.com",
      },
    }

    const transcriptResponse = await fetch(transcriptUrl, transcriptOptions)
    const transcriptResult = await transcriptResponse.json()

    if (!transcriptResult.success || !transcriptResult.transcript) {
      throw new Error("Failed to fetch video transcript")
    }

    setNotesCreationStep("Creating notes from transcript...")
    showToast("Creating notes from transcript...", "info")

    // Format transcript for the API call
    const formattedTranscript = transcriptResult.transcript.map((item) => ({
      text: item.text,
    }))

    // Get required data from localStorage
    const teacherId = localStorage.getItem("teacherId")
    const classRoomId = localStorage.getItem("classRoomId")
    if (!teacherId || !classRoomId) {
      throw new Error("Teacher ID or Classroom ID not found")
    }

    // API call to create-subtopic-notes-from-youtube
    const notesResponse = await postAws("https://api-dev.aurait.ai/eLearning", "POST", {
      request: "create-subtopic-notes-from-youtube",
      body: {
        courseId: courseId,
        topicId: currentVideoData.topicId,
        subtopicTitle: notesTitle,
        subtopicDescription: `AI-generated notes from ${currentVideoData.subtopicTitle}`,
        teacherId: teacherId,
        classroomId: classRoomId,
        courseTitle: course.name,
        topic: currentVideoData.topicTitle,
        subtopic: notesTitle,
        foldername: "notes",
        subtitles: formattedTranscript,
        file: null,
      },
    })

    if (notesResponse.statusCode === 200) {
      // 🛠️ Parse response body safely
      const responseBody =
        typeof notesResponse.body === "string"
          ? JSON.parse(notesResponse.body)
          : notesResponse.body

      const newSubtopicId = responseBody.subtopic?.id
      if (!newSubtopicId) {
        throw new Error("Missing subtopic ID in response")
      }

      setCreatedNotesId(newSubtopicId)

      setNotesCreationStep("Notes creation initiated! AI is processing your content...")
      showToast("AI is creating your notes, please wait. This may take 2–3 minutes.", "info")

      // Close dialog and reset
      setShowNotesDialog(false)
      setNotesTitle("")
      setCurrentVideoData(null)

      // Update UI with in-progress placeholder
      updateCourse((prev) => ({
        ...prev,
        topics: prev.topics.map((topic) =>
          topic.id === currentVideoData.topicId
            ? {
                ...topic,
                content: [
                  ...(topic.content || []),
                  {
                    id: newSubtopicId,
                    title: notesTitle,
                    description: `AI-generated notes from ${currentVideoData.subtopicTitle}`,
                    type: "pdf",
                    url: null,
                    s3Uri: null,
                    notesStatus: "in-progress",
                    rawJsonUrl: null,
                  },
                ],
              }
            : topic
        ),
      }))

      // Start polling
      startNotesStatusPolling(newSubtopicId)

      // Refresh course after short delay
      setTimeout(() => {
        fetchCourseDetails()
      }, 2000)
    } else {
      throw new Error(notesResponse.body?.message || "Failed to create notes from video")
    }
  } catch (error) {
    console.error("Error creating notes from video:", error)
    showToast("Error creating notes from video. Please try again.", "error")
  } finally {
    setIsCreatingNotes(false)
    setNotesCreationStep("")
  }
}


  const validateAndPublish = () => {
    // Check if there's at least one topic
    if (!course?.topics || course.topics.length === 0) {
      showToast("Cannot publish: Course must have at least one topic", "error")
      return
    }

    // Validate each topic has at least one subtopic and each subtopic has content
    const invalidTopics = []
    for (const topic of course.topics) {
      if (!topic.content || topic.content.length === 0) {
        invalidTopics.push({
          topicId: topic.id,
          topicTitle: topic.title,
          error: "No subtopics",
        })
        continue
      }

      // Check each subtopic for content
      for (const subtopic of topic.content) {
        if (subtopic.type === "video" && !subtopic.url) {
          invalidTopics.push({
            topicId: topic.id,
            topicTitle: topic.title,
            subtopicId: subtopic.id,
            subtopicTitle: subtopic.title,
            error: "Missing video URL",
          })
        } else if (subtopic.type === "pdf" && !subtopic.url && subtopic.notesStatus !== "in-progress") {
          invalidTopics.push({
            topicId: topic.id,
            topicTitle: topic.title,
            subtopicId: subtopic.id,
            subtopicTitle: subtopic.title,
            error: "Missing PDF URL",
          })
        } else if (
          subtopic.type === "quiz" &&
          (!subtopic.quizData || (Array.isArray(subtopic.quizData) && subtopic.quizData.length === 0))
        ) {
          invalidTopics.push({
            topicId: topic.id,
            topicTitle: topic.title,
            subtopicId: subtopic.id,
            subtopicTitle: subtopic.title,
            error: "Quiz is not prepared yet",
          })
        }
      }
    }

    if (invalidTopics.length > 0) {
      // Format error message for tooltip
      const errorMessages = invalidTopics.map((item) => {
        if (!item.subtopicId) {
          return `Topic "${item.topicTitle}": ${item.error}`
        }
        return `Topic "${item.topicTitle}" > Subtopic "${item.subtopicTitle}": ${item.error}`
      })
      showToast("Cannot publish: Some content is missing or incomplete", "error")
      // Set tooltip content
      setPublishTooltipContent(
        <div className="max-w-md">
          <p className="font-medium mb-2">Cannot publish due to the following issues:</p>
          <ul className="list-disc pl-5 space-y-1">
            {errorMessages.map((msg, idx) => (
              <li key={idx} className="text-sm">
                {msg}
              </li>
            ))}
          </ul>
        </div>,
      )
      return
    }

    // If validation passes, show student selection modal
    setShowStudentSelectionModal(true)
    fetchEnrolledStudents()
  }

  const handleStudentSelection = (studentId) => {
    setSelectedStudents((prev) => {
      if (prev.includes(studentId)) {
        return prev.filter((id) => id !== studentId)
      } else {
        return [...prev, studentId]
      }
    })
  }

  const handleSelectAllStudents = () => {
    if (selectedStudents.length === enrolledStudents.length) {
      setSelectedStudents([])
    } else {
      setSelectedStudents(enrolledStudents.map((student) => student.studentId))
    }
  }

  const handleShareWithStudents = async () => {
    if (selectedStudents.length === 0) {
      showToast("Please select at least one student to share the course with", "error")
      return
    }

    try {
      setIsSharing(true)
      showToast("Sharing course with selected students...", "info")
      const classRoomId = localStorage.getItem("classRoomId")
      if (!classRoomId) {
        throw new Error("Classroom ID not found")
      }

      const response = await postAws("https://api-dev.aurait.ai/eLearning", "POST", {
        request: "share-with-students-update",
        body: {
          classRoomId: classRoomId,
          courseId: courseId,
          studentIds: selectedStudents,
        },
      })

      if (response.statusCode === 200) {
        const selectedStudentNames = enrolledStudents
          .filter((student) => selectedStudents.includes(student.studentId))
          .map((student) => student.studentName)
        showToast(`Course has been shared with ${selectedStudentNames.join(", ")}`, "success")
        setShowStudentSelectionModal(false)
        setSelectedStudents([])
      } else {
        throw new Error(response.body?.message || "Failed to share course with students")
      }
    } catch (error) {
      console.error("Error sharing course with students:", error)
      showToast(`Error sharing course: ${error.message}`, "error")
    } finally {
      setIsSharing(false)
    }
  }

  const selectContent = (topicId, contentId) => {
    setActiveContentTab({ topicId, contentId })
  }

  const openQuizEditModal = (topicId, quiz) => {
    setQuizToEdit({
      topicId,
      quizData: quiz,
    })
  }

  const closeQuizEditModal = () => {
    setQuizToEdit(null)
  }

  const contentManagement = ContentManagement({
    courseId,
    course,
    updateCourse,
    unsavedTopics,
    setUnsavedTopics,
    showToast,
    currentTopic,
    setCurrentTopic,
    activeContentTab,
    setActiveContentTab,
    previewMode,
    selectContent,
    fetchCourseDetails,
    handleCreateNotesFromVideo,
  })

  const aiFeatures = AIFeatures({
    courseId,
    course,
    updateCourse,
    showToast,
    fetchCourseDetails,
    currentTopic,
  })

  const handleNotesDialogClose = useCallback(() => {
    setShowNotesDialog(false)
    setNotesTitle("")
    setCurrentVideoData(null)
    setNotesCreationStep("")
  }, [])

  const handleNotesTitleChange = useCallback((e) => {
    setNotesTitle(e.target.value)
  }, [])

  // Enhanced Notes Status Indicator Component
  const NotesStatusIndicator = ({ notesStatus, className = "" }) => {
    switch (notesStatus) {
      case "in-progress":
        return (
          <div className={`flex items-center text-orange-600 ${className}`}>
            <Clock className="h-3 w-3 mr-1" />
            <span className="text-xs">Creating...</span>
          </div>
        )
      case "completed":
        return (
          <div className={`flex items-center text-green-600 ${className}`}>
            <CheckCircle className="h-3 w-3 mr-1" />
            <span className="text-xs">Ready</span>
          </div>
        )
      case "failed":
        return (
          <div className={`flex items-center text-red-600 ${className}`}>
            <XCircle className="h-3 w-3 mr-1" />
            <span className="text-xs">Failed</span>
          </div>
        )
      default:
        return null
    }
  }

  // Enhanced Notes Creation Dialog Component
  const NotesCreationDialog = useCallback(() => {
    if (!showNotesDialog) return null

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-md w-full mx-4">
          <div className="p-6 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">Create Notes from Video</h2>
              <button onClick={handleNotesDialogClose} className="text-gray-500 hover:text-gray-700">
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              <div>
                <label htmlFor="notes-title" className="block text-sm font-medium text-gray-700 mb-1">
                  Enter name for the notes:
                </label>
                <input
                  key="notes-title-input"
                  id="notes-title"
                  type="text"
                  value={notesTitle}
                  onChange={handleNotesTitleChange}
                  autoFocus
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="e.g., Introduction to Physics - Notes"
                />
              </div>
              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Video:</strong> {currentVideoData?.subtopicTitle}
                </p>
                <p className="text-sm text-blue-600 mt-1">
                  AI will analyze the video transcript and create comprehensive notes for you.
                </p>
              </div>
              {notesCreationStep && (
                <div className="bg-green-50 p-3 rounded-lg">
                  <div className="flex items-center">
                    <Loader className="h-4 w-4 mr-2 animate-spin text-green-600" />
                    <p className="text-sm text-green-800">{notesCreationStep}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="p-6 border-t border-gray-200">
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleNotesDialogClose}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
                disabled={isCreatingNotes}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateNotes}
                disabled={!notesTitle.trim() || isCreatingNotes}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
              >
                {isCreatingNotes ? (
                  <>
                    <Loader className="h-4 w-4 mr-2 animate-spin" />
                    Creating Notes...
                  </>
                ) : (
                  <>
                    <BookOpen className="h-4 w-4 mr-2" />
                    Create Notes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }, [showNotesDialog, notesTitle, currentVideoData, isCreatingNotes, notesCreationStep, handleNotesDialogClose, handleNotesTitleChange])

  // Student Selection Modal Component
  const StudentSelectionModal = () => {
    if (!showStudentSelectionModal) return null

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-hidden mx-4">
          <div className="p-6 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Share Course with Students</h2>
                <p className="text-gray-600 mt-1">Select students to share "{course?.name}" with</p>
              </div>
              <button
                onClick={() => {
                  setShowStudentSelectionModal(false)
                  setSelectedStudents([])
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>
          <div className="p-6">
            {isLoadingStudents ? (
              <div className="flex items-center justify-center py-8">
                <Loader className="h-8 w-8 animate-spin text-blue-600" />
                <span className="ml-3 text-gray-600">Loading students...</span>
              </div>
            ) : enrolledStudents.length === 0 ? (
              <div className="text-center py-8">
                <Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No enrolled students found in this class.</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900">Enrolled Students ({enrolledStudents.length})</h3>
                  <button
                    onClick={handleSelectAllStudents}
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    {selectedStudents.length === enrolledStudents.length ? "Deselect All" : "Select All"}
                  </button>
                </div>
                <div className="max-h-48 sm:max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
                  {enrolledStudents.map((student) => (
                    <div
                      key={student.studentId}
                      className="flex items-center p-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                    >
                      <div className="flex items-center flex-1">
                        <div
                          className={`w-5 h-5 rounded border-2 flex items-center justify-center cursor-pointer mr-3 ${
                            selectedStudents.includes(student.studentId)
                              ? "bg-blue-600 border-blue-600"
                              : "border-gray-300 hover:border-blue-400"
                          }`}
                          onClick={() => handleStudentSelection(student.studentId)}
                        >
                          {selectedStudents.includes(student.studentId) && <Check className="h-3 w-3 text-white" />}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{student.studentName}</p>
                          <p className="text-sm text-gray-500">{student.studentEmail}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {selectedStudents.length > 0 && (
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>{selectedStudents.length}</strong> student{selectedStudents.length !== 1 ? "s" : ""}{" "}
                      selected
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="p-6 border-t border-gray-200">
            <div className="flex flex-wrap justify-end gap-2">
              <button
                onClick={() => {
                  setShowStudentSelectionModal(false)
                  setSelectedStudents([])
                }}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleShareWithStudents}
                disabled={selectedStudents.length === 0 || isSharing}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
              >
                {isSharing ? (
                  <>
                    <Loader className="h-4 w-4 mr-2 animate-spin" />
                    Sharing...
                  </>
                ) : (
                  <>
                    <Users className="h-4 w-4 mr-2" />
                    Share with {selectedStudents.length} Student{selectedStudents.length !== 1 ? "s" : ""}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center h-screen bg-gradient-to-b from-blue-50 to-white">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-600 mb-4"></div>
        <p className="text-lg text-gray-600 font-medium">Loading your course...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gray-50 p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Error</h2>
          <p className="text-gray-600 mb-8">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition duration-300 shadow-md"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30 shadow-sm">
        <div className="max-w-full mx-auto px-4">
          <div className="flex justify-between items-center h-16">
            {/* Left side - Back button */}
            <button
              onClick={() => window.history.back()}
              className="flex items-center text-gray-700 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
              <span className="ml-2 font-medium">Back</span>
            </button>
            {/* Right side - Preview and Publish buttons */}
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setPreviewMode(!previewMode)}
                className={`flex items-center px-3 py-2 rounded-md text-sm font-medium ${
                  previewMode ? "bg-blue-100 text-blue-700" : "text-gray-700 hover:bg-gray-100"
                } transition-colors`}
              >
                <Eye className="h-4 w-4 mr-2" />
                {previewMode ? "Exit Preview" : "Preview"}
              </button>
              <button
                onClick={() => validateAndPublish()}
                className="flex items-center px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
                data-tooltip-id="publish-tooltip"
              >
                <ArrowUpRight className="h-4 w-4 mr-2" />
                Publish
              </button>
              {/* Mobile Menu Button */}
              <button
                onClick={() => setShowMobileMenu(true)}
                className="md:hidden flex items-center text-gray-700 hover:text-gray-900 transition-colors"
              >
                <MoreVertical className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className="flex flex-col md:flex-row flex-grow">
        {/* Desktop Sidebar - Hidden on Mobile */}
        <div className="hidden md:block w-80 border-r border-gray-200 h-[calc(100vh-64px)] overflow-y-auto bg-white shadow-inner flex-shrink-0">
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <div className="bg-white border border-gray-200 rounded-lg p-4 flex flex-col items-center justify-center h-48 relative overflow-hidden shadow-sm">
              {course?.thumbnail ? (
                <>
                  <img
                    src={course.thumbnail || "/placeholder.svg"}
                    alt="Course thumbnail"
                    className="w-full h-full object-cover absolute inset-0"
                  />
                  {!previewMode && (
                    <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => handleThumbnailUpload()}
                        className="bg-white text-gray-800 p-2 rounded-full hover:bg-gray-100 transition-colors"
                      >
                        <Edit2 className="h-5 w-5" />
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <CloudUpload className="h-12 w-12 text-gray-400 mb-2" />
                  <p className="text-sm text-gray-500 text-center">Add course cover</p>
                  {!previewMode && (
                    <button
                      onClick={() => handleThumbnailUpload()}
                      className="mt-3 px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors shadow-sm"
                    >
                      Upload thumbnail
                    </button>
                  )}
                </>
              )}
            </div>
            <div className="mt-4">
              <h1 className="text-xl font-bold text-gray-900">{course?.name || "Untitled Course"}</h1>
            </div>
          </div>
          <TopicManagement
            course={course}
            unsavedTopics={unsavedTopics}
            updateCourse={updateCourse}
            setUnsavedTopics={setUnsavedTopics}
            showToast={showToast}
            courseId={courseId}
            previewMode={previewMode}
            openContentMenu={contentManagement?.openContentMenu}
            selectContent={selectContent}
            getContentIcon={getContentIcon}
            handleDeleteSubtopic={contentManagement?.handleDeleteSubtopic}
            handleSequenceChange={handleSequenceChange}
            NotesStatusIndicator={NotesStatusIndicator}
          />
        </div>

        {/* Main Content Area */}
        <div className="flex-1 p-6 overflow-y-auto">
          {activeContentTab ? (
            <div className="max-w-4xl mx-auto">
              {(() => {
                if (!course) return <div className="text-gray-500">Loading...</div>
                const topic = [...(course?.topics || []), ...unsavedTopics].find(
                  (t) => t.id === activeContentTab.topicId,
                )
                if (!topic) return <div className="text-gray-500">Content not found</div>
                const content = topic.content?.find((c) => c.id === activeContentTab.contentId)
                if (!content) return <div className="text-gray-500">Content not found</div>

                return (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                      <div>
                        <div className="flex items-center text-sm text-gray-500 mb-1">
                          <span>{topic.title}</span>
                          <span className="mx-2">•</span>
                          <div className="flex items-center">
                            {getContentIcon(content.type)}
                            <span className="ml-1 capitalize">{content.type}</span>
                          </div>
                          {content.notesStatus && (
                            <>
                              <span className="mx-2">•</span>
                              <NotesStatusIndicator notesStatus={content.notesStatus} />
                            </>
                          )}
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900">{content.title}</h2>
                      </div>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                      {content.description ? (
                        <TruncatedDescription text={content.description} maxLength={120} />
                      ) : !previewMode ? (
                        <div>
                          {content.isGeneratingDescription ? (
                            <div className="flex items-center text-gray-500">
                              <Loader className="animate-spin h-4 w-4 mr-2" />
                              <span>Generating description...</span>
                            </div>
                          ) : (
                            <div className="flex items-center">
                              <p className="text-gray-500 italic mr-2">No description provided.</p>
                              <button
                                onClick={() =>
                                  contentManagement?.generateDescriptionWithAI &&
                                  contentManagement.generateDescriptionWithAI(
                                    activeContentTab.topicId,
                                    content.id,
                                    content.title,
                                  )
                                }
                                className="px-3 py-1 bg-purple-600 text-white text-xs rounded-md hover:bg-purple-700 transition-colors inline-flex items-center"
                              >
                                <WandIcon className="h-3 w-3 mr-1" />
                                Create with AI
                              </button>
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="text-gray-500 italic">No description provided.</p>
                      )}
                    </div>
                    <div className="mt-6 bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                      {/* Use the renderContent function from contentManagement */}
                      {contentManagement?.renderContent && contentManagement.renderContent(content)}
                    </div>
                  </div>
                )
              })()}
            </div>
          ) : (
            <div className="max-w-4xl mx-auto">
              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Course Overview</h2>
                {isEditingTitle ? (
                  <div className="mb-6">
                    <label htmlFor="course-title" className="block text-sm font-medium text-gray-700 mb-1">
                      Course Title
                    </label>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
                      <input
                        id="course-title"
                        type="text"
                        value={editedTitle}
                        onChange={(e) => setEditedTitle(e.target.value)}
                        className="flex-1 p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        placeholder="Enter course title"
                      />
                      <div className="flex flex-row sm:flex-col gap-2 sm:ml-2 mt-2 sm:mt-0">
                        <button
                          onClick={handleSaveTitle}
                          disabled={isSaving}
                          className="p-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                        >
                          {isSaving ? <Loader className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />}
                        </button>
                        <button
                          onClick={() => {
                            setIsEditingTitle(false)
                            setEditedTitle(course?.name || "")
                          }}
                          className="p-2 ml-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
                        >
                          <X className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-between items-center mb-6 bg-gray-50 p-4 rounded-lg">
                    <h3 className="text-xl font-semibold text-gray-900">{course?.name || "Untitled Course"}</h3>
                    <button
                      onClick={() => {
                        setIsEditingTitle(true)
                        setEditedTitle(course?.name || "")
                      }}
                      className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
                    >
                      <Edit2 className="h-5 w-5" />
                    </button>
                  </div>
                )}
                {isEditingDescription ? (
                  <div>
                    <label htmlFor="course-description" className="block text-sm font-medium text-gray-700 mb-1">
                      Course Description
                    </label>
                    <div className="flex flex-col sm:flex-row items-start gap-2">
                      <textarea
                        id="course-description"
                        value={editedDescription}
                        onChange={(e) => setEditedDescription(e.target.value)}
                        className="flex-1 p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors min-h-[120px]"
                        placeholder="Enter course description..."
                      />
                      <div className="flex flex-row sm:flex-col gap-2 sm:ml-2 mt-2 sm:mt-0">
                        <button
                          onClick={handleSaveDescription}
                          disabled={isSaving}
                          className="p-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                        >
                          {isSaving ? <Loader className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />}
                        </button>
                        <button
                          onClick={() => {
                            setIsEditingDescription(false)
                            setEditedDescription(course?.description || "")
                          }}
                          className="p-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
                        >
                          <X className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-between items-start bg-gray-50 p-4 rounded-lg">
                    <div className="flex-1">
                      <TruncatedDescription text={course?.description || ""} maxLength={120} />
                    </div>
                    <button
                      className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors ml-2 flex-shrink-0"
                      onClick={() => {
                        setIsEditingDescription(true)
                        setEditedDescription(course?.description || "")
                      }}
                    >
                      <Edit2 className="h-5 w-5" />
                    </button>
                  </div>
                )}
                <div className="mt-8">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-medium text-gray-900">Chapter Overview</h4>
                  </div>
                  {course?.topics && course.topics.length > 0 ? (
                    <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                      {course.topics.map((topic) => (
                        <div
                          key={topic.id}
                          className="border border-gray-200 rounded-lg bg-white shadow-sm hover:shadow-md transition-shadow overflow-hidden cursor-pointer"
                          onClick={() =>
                            topic.content && topic.content.length > 0 && selectContent(topic.id, topic.content[0].id)
                          }
                        >
                          <div className="p-4 border-b border-gray-100">
                            <h5 className="font-medium text-gray-900 flex items-center">
                              <BookOpen className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                              <span className="truncate">{topic.title}</span>
                            </h5>
                          </div>
                          <div className="p-4">
                            {topic.content && topic.content.length > 0 ? (
                              <div className="space-y-3">
                                <div className="flex flex-wrap gap-2">
                                  <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                                    {topic.content.length} {topic.content.length === 1 ? "item" : "items"}
                                  </span>
                                  {topic.content.some((c) => c.type === "video") && (
                                    <span className="bg-red-100 text-red-800 text-xs font-medium px-2.5 py-0.5 rounded-full flex items-center">
                                      <Film size={10} className="mr-1" />
                                      {topic.content.filter((c) => c.type === "video").length}
                                    </span>
                                  )}
                                  {topic.content.some((c) => c.type === "pdf") && (
                                    <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full flex items-center">
                                      <FileText size={10} className="mr-1" />
                                      {topic.content.filter((c) => c.type === "pdf").length}
                                    </span>
                                  )}
                                  {topic.content.some((c) => c.type === "quiz") && (
                                    <span className="bg-purple-100 text-purple-800 text-xs font-medium px-2.5 py-0.5 rounded-full flex items-center">
                                      <FileQuestion size={10} className="mr-1" />
                                      {topic.content.filter((c) => c.type === "quiz").length}
                                    </span>
                                  )}
                                </div>
                                <div className="space-y-1.5 mt-3">
                                  {topic.content.slice(0, 3).map((item) => (
                                    <div
                                      key={item.id}
                                      className="flex items-center text-sm text-gray-600 hover:text-blue-600 cursor-pointer"
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        selectContent(topic.id, item.id)
                                      }}
                                    >
                                      {getContentIcon(item.type)}
                                      <span className="ml-1.5 truncate">{item.title}</span>
                                      {item.notesStatus && (
                                        <NotesStatusIndicator notesStatus={item.notesStatus} className="ml-auto" />
                                      )}
                                    </div>
                                  ))}
                                  {topic.content.length > 3 && (
                                    <div className="text-xs text-blue-600 font-medium mt-1">
                                      + {topic.content.length - 3} more{" "}
                                      {topic.content.length - 3 === 1 ? "item" : "items"}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-center justify-center h-16">
                                <span className="text-sm text-gray-400 italic">No content added yet</span>
                              </div>
                            )}
                          </div>
                          {!previewMode && (
                            <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 flex justify-end">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  contentManagement?.openContentMenu && contentManagement.openContentMenu(topic.id)
                                }}
                                className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center"
                              >
                                <Plus className="h-3 w-3 mr-1" />
                                Add content
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <EmptyCourseView
                      courseId={courseId}
                      showToast={showToast}
                      fetchCourseDetails={fetchCourseDetails}
                      generateCourseFromYouTubePlaylist={contentManagement?.generateCourseFromYouTubePlaylist}
                      generateCourseOutlineWithAI={aiFeatures?.generateCourseOutlineWithAI}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Menu Modal */}
      {showMobileMenu && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex z-50 md:hidden">
          <div className="bg-white w-80 max-w-[85vw] h-full overflow-y-auto shadow-xl">
            {/* Modal Header */}
            <div className="p-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900">Course Contents</h2>
              <button
                onClick={() => setShowMobileMenu(false)}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            {/* Course Thumbnail */}
            <div className="p-4 border-b border-gray-200 bg-gray-50">
              <div className="bg-white border border-gray-200 rounded-lg p-4 flex flex-col items-center justify-center h-32 relative overflow-hidden shadow-sm">
                {course?.thumbnail ? (
                  <>
                    <img
                      src={course.thumbnail || "/placeholder.svg"}
                      alt="Course thumbnail"
                      className="w-full h-full object-cover absolute inset-0"
                    />
                    {!previewMode && (
                      <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => handleThumbnailUpload()}
                          className="bg-white text-gray-800 p-2 rounded-full hover:bg-gray-100 transition-colors"
                        >
                          <Edit2 className="h-5 w-5" />
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <CloudUpload className="h-8 w-8 text-gray-400 mb-2" />
                    <p className="text-xs text-gray-500 text-center">Add course cover</p>
                    {!previewMode && (
                      <button
                        onClick={() => handleThumbnailUpload()}
                        className="mt-2 px-2 py-1 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors shadow-sm"
                      >
                        Upload thumbnail
                      </button>
                    )}
                  </>
                )}
              </div>
              <div className="mt-2">
                <h1 className="text-sm font-bold text-gray-900">{course?.name || "Untitled Course"}</h1>
              </div>
            </div>
            {/* Mobile Topic Management */}
            <div className="p-4">
              <div className="space-y-2">
                {[...(course?.topics || []), ...unsavedTopics].map((topic) => (
                  <div key={topic.id} className="rounded-md">
                    <div
                      className={`flex items-center p-2.5 rounded-md cursor-pointer ${
                        topic.isExpanded ? "bg-blue-50 text-blue-700" : "hover:bg-gray-100 text-gray-700"
                      } transition-colors`}
                      onClick={() => {
                        if (topic.id.startsWith("unsaved-")) {
                          setUnsavedTopics(
                            unsavedTopics.map((t) => (t.id === topic.id ? { ...t, isExpanded: !t.isExpanded } : t)),
                          )
                        } else {
                          updateCourse((prev) => ({
                            ...prev,
                            topics: prev.topics.map((t) =>
                              t.id === topic.id ? { ...t, isExpanded: !t.isExpanded } : t,
                            ),
                          }))
                        }
                      }}
                    >
                      {topic.isExpanded ? (
                        <ChevronDown className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-gray-500 mr-2 flex-shrink-0" />
                      )}
                      <BookOpen
                        className={`h-4 w-4 ${topic.isExpanded ? "text-blue-500" : "text-gray-500"} mr-2 flex-shrink-0`}
                      />
                      <span
                        className={`text-sm font-medium ${topic.isExpanded ? "text-blue-700" : "text-gray-700"} truncate`}
                      >
                        {topic.title}
                      </span>
                    </div>
                    {topic.isExpanded && (
                      <div className="ml-8 mt-1 space-y-1 pl-2 border-l-2 border-gray-100">
                        {(topic.content || []).map((item) => (
                          <div
                            key={item.id}
                            className={`flex items-center p-2 rounded-md cursor-pointer text-sm ${
                              activeContentTab?.contentId === item.id
                                ? "bg-blue-100 text-blue-700"
                                : "hover:bg-gray-50 text-gray-600"
                            } transition-colors`}
                            onClick={() => {
                              selectContent(topic.id, item.id)
                              setShowMobileMenu(false)
                            }}
                          >
                            {getContentIcon(item.type)}
                            <span className="ml-2 truncate">{item.title}</span>
                            {item.notesStatus && (
                              <div className="ml-auto">
                                <NotesStatusIndicator notesStatus={item.notesStatus} />
                              </div>
                            )}
                          </div>
                        ))}
                        {!previewMode && (
                          <button
                            onClick={() => {
                              contentManagement.openContentMenu(topic.id)
                              setShowMobileMenu(false)
                            }}
                            className="flex items-center p-2 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors w-full"
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Add content
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
          {/* Overlay to close modal */}
          <div className="flex-1" onClick={() => setShowMobileMenu(false)} />
        </div>
      )}

      {/* Modals */}
      {contentManagement?.showContentMenu && contentManagement.showContentMenu.startsWith("add-") && (
        <ContentMenuModal contentManagement={contentManagement || {}} aiFeatures={aiFeatures} />
      )}
      {contentManagement?.isContentModalOpen && (
        <ContentModal
          isContentModalOpen={contentManagement.isContentModalOpen}
          closeContentModal={contentManagement.closeContentModal}
          contentType={contentManagement.contentType}
          videoData={contentManagement.videoData}
          setVideoData={contentManagement.setVideoData}
          pdfData={contentManagement.pdfData}
          setPdfData={contentManagement.setPdfData}
          quizData={contentManagement.quizData}
          setQuizData={contentManagement.setQuizData}
          handleVideoSubmit={contentManagement.handleVideoSubmit}
          handlePdfSubmit={contentManagement.handlePdfSubmit}
          handleQuizSubmit={contentManagement.handleQuizSubmit}
          handleVideoFileChange={contentManagement.handleVideoFileChange}
          handlePdfFileChange={contentManagement.handlePdfFileChange}
          handleQuizQuestionChange={contentManagement.handleQuizQuestionChange}
          handleQuizOptionChange={contentManagement.handleQuizOptionChange}
          addQuizQuestion={contentManagement.addQuizQuestion}
          removeQuizQuestion={contentManagement.removeQuizQuestion}
          uploadProgress={contentManagement.uploadProgress}
          addingSubtopicId={contentManagement.addingSubtopicId}
          fileInputRef={contentManagement.fileInputRef}
          pdfInputRef={contentManagement.pdfInputRef}
          youtubeInputRef={contentManagement.youtubeInputRef}
          aiFeatures={aiFeatures}
        />
      )}
      {contentManagement?.isEditingSubtopic && contentManagement.editingSubtopicData && (
        <EditSubtopicModal contentManagement={contentManagement} />
      )}
      {aiFeatures?.showOutlineConfirmation && aiFeatures.generatedOutline && (
        <CourseOutlineConfirmationModal aiFeatures={aiFeatures} />
      )}

      {/* Notes Editor Modal */}
      <NotesEditorModal
        isOpen={contentManagement?.isEditingNotes || false}
        onClose={contentManagement?.closeNotesEditor}
        notesData={contentManagement?.editingNotesData}
        jsonData={contentManagement?.notesJsonData}
        isLoading={contentManagement?.isLoadingNotesJson || false}
        isSaving={contentManagement?.isSavingNotes || false}
        onUpdateContent={contentManagement?.updateNotesContent}
        onUpdateTitle={contentManagement?.updateNotesTitle}
        onSave={contentManagement?.saveEditedNotes}
      />

      {/* Quiz Edit Modal */}
      {quizToEdit && (
        <CourseQuizEditModal
          isOpen={!!quizToEdit}
          onClose={closeQuizEditModal}
          quiz={quizToEdit.quizData}
          topicId={quizToEdit.topicId}
          courseId={courseId}
          updateCourse={updateCourse}
          showToast={showToast}
        />
      )}

      {/* Enhanced Notes Creation Dialog */}
      <NotesCreationDialog />

      {/* Student Selection Modal */}
      <StudentSelectionModal />

      {/* Tooltip */}
      <div
        id="publish-tooltip"
        className="absolute bg-gray-800 text-white p-3 rounded-lg shadow-lg z-50 max-w-sm"
        style={{ display: "none" }}
      >
        {publishTooltipContent}
      </div>
    </div>
  )
}